import { SiteHeader } from "@/components/site-header"

export default function ShipmentMobileFlowPage() {
  return (
    <>
      <SiteHeader title="Cargas y Viajes — Asignación de Móvil" />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">
        <div className="max-w-5xl space-y-12">

          {/* ── Resumen ── */}
          <section>
            <h2 className="text-base font-semibold mb-2">Descripción general</h2>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Un <strong>Móvil</strong> ({" "}
              <code className="text-xs bg-muted px-1.5 py-0.5 rounded">mobiles</code>) agrupa a un
              chofer, un camión y un acoplado/semi bajo una misma unidad operativa. Cada móvil tiene
              restricciones de unicidad: un chofer, camión o semi solo puede pertenecer a un móvil a
              la vez. Cuando se crea o edita una Carga o Viaje, el sistema copia el chofer, camión y
              acoplado del móvil directamente al registro del viaje — lo que permite que los campos
              del viaje queden explícitos e independientes del móvil para el historial.
            </p>
          </section>

          {/* ── Restricciones de unicidad ── */}
          <section>
            <h2 className="text-base font-semibold mb-4">Unicidad en la tabla <code className="text-xs bg-muted px-1.5 py-0.5 rounded">mobiles</code></h2>
            <div className="rounded-lg border overflow-hidden text-sm">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground w-40">Campo</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground w-32">Constraint DB</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Error al violar</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr>
                    <td className="px-4 py-3 font-mono text-xs">driver_id</td>
                    <td className="px-4 py-3"><code className="text-xs bg-muted px-1 py-0.5 rounded">UNIQUE</code></td>
                    <td className="px-4 py-3 text-muted-foreground">El chofer ya está asignado a otro móvil.</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-mono text-xs">truck_id</td>
                    <td className="px-4 py-3"><code className="text-xs bg-muted px-1 py-0.5 rounded">UNIQUE</code></td>
                    <td className="px-4 py-3 text-muted-foreground">El camión ya está asignado a otro móvil.</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-mono text-xs">trailer_id</td>
                    <td className="px-4 py-3"><code className="text-xs bg-muted px-1 py-0.5 rounded">UNIQUE</code></td>
                    <td className="px-4 py-3 text-muted-foreground">El semi/tráiler ya está asignado a otro móvil.</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              La constraint sigue existiendo en la DB como garantía de integridad, pero el endpoint
              {" "}<code className="bg-muted px-1 py-0.5 rounded">PATCH /api/mobiles/:id</code> ya no la viola:
              detecta previamente qué recursos serán "robados" y los libera del móvil origen dentro
              de una <strong>transacción atómica</strong> antes de asignarlos al destino. Ver sección de Reasignación más abajo.
            </p>
          </section>

          {/* ── Estado del móvil ── */}
          <section>
            <h2 className="text-base font-semibold mb-4">Estado de actividad del móvil</h2>
            <p className="text-sm text-muted-foreground mb-3">
              Un móvil es <strong>activo</strong> cuando cumple simultáneamente las tres condiciones:
            </p>
            <div className="rounded-lg border overflow-hidden text-sm">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Recurso</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Condición requerida</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr>
                    <td className="px-4 py-3">Chofer</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      <code className="text-xs bg-muted px-1 py-0.5 rounded">driver_id IS NOT NULL</code> y{" "}
                      <code className="text-xs bg-muted px-1 py-0.5 rounded">deleted_at IS NULL</code> y{" "}
                      <code className="text-xs bg-muted px-1 py-0.5 rounded">driver_status = 'active'</code>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3">Camión</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      <code className="text-xs bg-muted px-1 py-0.5 rounded">truck_id IS NOT NULL</code> y{" "}
                      <code className="text-xs bg-muted px-1 py-0.5 rounded">deleted_at IS NULL</code> y{" "}
                      <code className="text-xs bg-muted px-1 py-0.5 rounded">status = 'active'</code>
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3">Semi/Tráiler</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      <code className="text-xs bg-muted px-1 py-0.5 rounded">trailer_id IS NOT NULL</code> y{" "}
                      <code className="text-xs bg-muted px-1 py-0.5 rounded">deleted_at IS NULL</code> y{" "}
                      <code className="text-xs bg-muted px-1 py-0.5 rounded">status = 'active'</code>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              En los selectores de la UI, los móviles inactivos aparecen <strong>deshabilitados</strong> con
              el motivo en color ámbar (ej. "sin chofer", "sin semi"). Los activos muestran avatar del
              operador + código M{"{N}"} + nombre del chofer.
            </p>
          </section>

          {/* ── Flujo al crear/editar carga ── */}
          <section>
            <h2 className="text-base font-semibold mb-4">Flujo al crear o editar una Carga</h2>
            <ol className="space-y-3 text-sm">
              {[
                {
                  n: "1",
                  title: "Seleccionar móvil",
                  desc: "Al elegir un móvil en el formulario, el sistema registra su mobileId. Al guardar, el API crea automáticamente el primer viaje (POST /api/trips) con el mobileId + driverId + truckId + trailerId del móvil copiados directamente al trip.",
                },
                {
                  n: "2",
                  title: "Editar carga con un solo viaje",
                  desc: "Si la carga tiene exactamente 1 viaje (tripCount = 1) y se cambia el móvil, el viaje existente se actualiza con el nuevo mobileId + los campos del nuevo móvil. Si la carga tiene 2+ viajes, el selector de móvil queda deshabilitado y se muestra un aviso.",
                },
                {
                  n: "3",
                  title: "Modificar móvil desde la carga",
                  desc: 'El link "Modificar móvil" (solo visible con 1 viaje) despliega un panel con los selects de Chofer y Semi del móvil. Al guardar, se hace PATCH /api/mobiles/:id para actualizar el móvil, LUEGO se actualiza el viaje con los nuevos driverId/trailerId, y finalmente se guarda la carga.',
                },
                {
                  n: "4",
                  title: "Confirmación antes de patchear el móvil",
                  desc: "Modificar el chofer o semi de un móvil afecta a todos los viajes que lo usen. El sistema muestra un diálogo de confirmación antes de aplicar el cambio.",
                },
              ].map(({ n, title, desc }) => (
                <li key={n} className="flex gap-3">
                  <span className="size-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold shrink-0 mt-0.5">{n}</span>
                  <div>
                    <p className="font-medium">{title}</p>
                    <p className="text-muted-foreground">{desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          {/* ── Flujo viajes ── */}
          <section>
            <h2 className="text-base font-semibold mb-4">Flujo al crear o editar un Viaje</h2>
            <ol className="space-y-3 text-sm">
              {[
                {
                  n: "1",
                  title: "Seleccionar móvil",
                  desc: "El formulario muestra todos los móviles. Al seleccionar uno activo, el sistema auto-rellena silenciosamente driverId, truckId y trailerId en el form (campos ocultos). Los campos Chofer, Camión y Semi no aparecen directamente — se accede a ellos via «Modificar móvil».",
                },
                {
                  n: "2",
                  title: "Columna Móvil en la tabla de viajes",
                  desc: "La columna Móvil muestra el código M{N} del camión (unitNumber del truck). La columna Camión/Semi muestra la patente del camión y la del acoplado. El Chofer muestra avatar + nombre. Todos estos datos vienen de trips.driverId, trips.truckId, trips.trailerId — sin fallback al móvil.",
                },
                {
                  n: "3",
                  title: "Modificar móvil desde un viaje",
                  desc: 'El mismo link "Modificar móvil" con panel de Chofer + Semi. Al confirmar: PATCH /api/mobiles/:id con los nuevos valores, y PATCH /api/trips/:id con los nuevos driverId/trailerId usando los valores del form (no los stale de mobileOptions).',
                },
              ].map(({ n, title, desc }) => (
                <li key={n} className="flex gap-3">
                  <span className="size-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-semibold shrink-0 mt-0.5">{n}</span>
                  <div>
                    <p className="font-medium">{title}</p>
                    <p className="text-muted-foreground">{desc}</p>
                  </div>
                </li>
              ))}
            </ol>
          </section>

          {/* ── Campos del viaje ── */}
          <section>
            <h2 className="text-base font-semibold mb-4">Campos directos en <code className="text-xs bg-muted px-1.5 py-0.5 rounded">trips</code></h2>
            <p className="text-sm text-muted-foreground mb-3">
              Desde este rediseño, los viajes almacenan sus propios FK directos (no solo el mobileId).
              El SELECT de la API <strong>no hace COALESCE</strong> al móvil — muestra exactamente lo que tiene el viaje.
            </p>
            <div className="rounded-lg border overflow-hidden text-sm">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground w-40">Campo</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground w-40">Se rellena cuando</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Fuente</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {[
                    ["mobile_id", "Siempre al crear/editar", "Selección del usuario"],
                    ["driver_id", "Al asignar o cambiar móvil", "mobile.driver_id en el momento de asignación"],
                    ["truck_id", "Al asignar o cambiar móvil", "mobile.truck_id en el momento de asignación"],
                    ["trailer_id", "Al asignar o cambiar móvil", "mobile.trailer_id en el momento de asignación"],
                  ].map(([field, when, source]) => (
                    <tr key={field}>
                      <td className="px-4 py-3 font-mono text-xs">{field}</td>
                      <td className="px-4 py-3 text-muted-foreground text-xs">{when}</td>
                      <td className="px-4 py-3 text-muted-foreground text-xs">{source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              <strong>operatorName</strong> sí se obtiene del móvil en runtime (JOIN mobiles → users) ya que
              el operador no cambia con frecuencia y no se guarda en el viaje.
            </p>
          </section>

          {/* ── Selects en UI ── */}
          <section>
            <h2 className="text-base font-semibold mb-4">Selectores de opciones ya asignadas</h2>
            <p className="text-sm text-muted-foreground mb-3">
              En el panel «Modificar móvil», los selects de Chofer y Semi calculan qué recursos
              ya están ocupados en otros móviles y los muestran deshabilitados:
            </p>
            <div className="rounded-lg border overflow-hidden text-sm">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Select</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Opciones deshabilitadas</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Sublabel informativo</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr>
                    <td className="px-4 py-3">Chofer</td>
                    <td className="px-4 py-3 text-muted-foreground">Ninguna — todos seleccionables</td>
                    <td className="px-4 py-3 font-mono text-xs">⚠️ En {'{'}patente camión{'}'} · {'{'}patente trailer{'}'}</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3">Camión</td>
                    <td className="px-4 py-3 text-muted-foreground">Ninguna — todos seleccionables</td>
                    <td className="px-4 py-3 font-mono text-xs">⚠️ En {'{'}nombre chofer{'}'} · {'{'}patente trailer{'}'}</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3">Semi / Tráiler</td>
                    <td className="px-4 py-3 text-muted-foreground">Ninguna — todos seleccionables</td>
                    <td className="px-4 py-3 font-mono text-xs">⚠️ En {'{'}nombre chofer{'}'} · {'{'}patente camión{'}'}</td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3">Eliminados</td>
                    <td className="px-4 py-3 text-muted-foreground">No aparecen en ningún selector</td>
                    <td className="px-4 py-3 text-muted-foreground">Banner rojo en el campo si el recurso actual estaba eliminado</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* ── Reasignación atómica ── */}
          <section>
            <h2 className="text-base font-semibold mb-3">Reasignación atómica de recursos</h2>
            <p className="text-sm text-muted-foreground mb-4 leading-relaxed">
              Cuando el operador selecciona un camión, semi o chofer que ya pertenece a <strong>otro móvil activo</strong>,
              el sistema ejecuta una reasignación atómica en dos pasos dentro de una ónica
              {" "}<code className="text-xs bg-muted px-1.5 py-0.5 rounded">db.transaction()</code>:
            </p>
            <ol className="text-sm text-muted-foreground space-y-2 list-decimal list-inside mb-4">
              <li>
                <strong>Preview (sin efecto)</strong>: al presionar "Guardar cambios", el frontend
                llama a <code className="text-xs bg-muted px-1.5 py-0.5 rounded">POST /api/mobiles/:id/reassign-preview</code>{" "}
                con solo los campos que cambiaron. El API responde con un array de conflictos, cada uno con:
                label del recurso, label del móvil origen, y cantidad de viajes activos en ese móvil.
                Si no hay conflictos, el guardado procede sin mostrar ningún dialog.
              </li>
              <li>
                <strong>Confirmación</strong>: si hay conflictos, aparece un dialog con la lista de
                cambios que se producirán. El operador puede cancelar o confirmar.
              </li>
              <li>
                <strong>Transacción</strong>: al confirmar, <code className="text-xs bg-muted px-1.5 py-0.5 rounded">PATCH /api/mobiles/:id</code>{" "}
                ejecuta dentro de una transacción: primero setea a{" "}
                <code className="text-xs bg-muted px-1.5 py-0.5 rounded">null</code> los campos en los
                móviles origen, luego actualiza el móvil destino. La constraint UNIQUE nunca falla.
              </li>
            </ol>
            <div className="rounded-md border bg-amber-50 dark:bg-amber-950/20 border-amber-200 dark:border-amber-800 px-4 py-3 text-sm text-amber-800 dark:text-amber-300">
              <strong>Viajes activos:</strong> el dialog de confirmación advierte si el móvil origen tiene viajes
              en estado <code className="text-xs bg-amber-100 dark:bg-amber-900 px-1 py-0.5 rounded">pendiente</code> o{" "}
              <code className="text-xs bg-amber-100 dark:bg-amber-900 px-1 py-0.5 rounded">en_curso</code>.
              Esos viajes <strong>no se modifican</strong>: sus campos
              {" "}<code className="text-xs bg-amber-100 dark:bg-amber-900 px-1 py-0.5 rounded">driver_id</code>,{" "}
              <code className="text-xs bg-amber-100 dark:bg-amber-900 px-1 py-0.5 rounded">truck_id</code> y{" "}
              <code className="text-xs bg-amber-100 dark:bg-amber-900 px-1 py-0.5 rounded">trailer_id</code>{" "}
              son un snapshot histórico independiente del móvil.
            </div>
          </section>

          {/* ── API endpoints ── */}
          <section>
            <h2 className="text-base font-semibold mb-4">Endpoints relevantes</h2>
            <div className="space-y-2 text-sm font-mono">
              {[
                { method: "POST",  path: "/api/shipments",         desc: "Crea el envío. Si mobileId presente, crea un trip automáticamente con los campos del móvil." },
                { method: "PATCH", path: "/api/shipments/:id",     desc: "Actualiza el envío. Si hay 1 solo trip y cambia el mobileId, actualiza el trip también." },
                { method: "POST",  path: "/api/trips",             desc: "Crea un viaje con mobileId + driverId + truckId + trailerId explícitos." },
                { method: "PATCH", path: "/api/trips/:id",         desc: "Actualiza el viaje. Acepta mobileId, driverId, truckId, trailerId. Solo sobreescribe driver/truck/trailer si mobileId cambió." },
                { method: "POST",  path: "/api/mobiles/:id/reassign-preview", desc: "Dry-run: detecta qué recursos serán movidos de otro móvil. Devuelve conflicts[] con label del móvil origen y cantidad de viajes activos. Sin efecto secundario." },
                { method: "PATCH", path: "/api/mobiles/:id",       desc: "Actualiza el móvil. Si un recurso (driver/truck/trailer) pertenece a otro móvil activo, lo libera primero en la misma db.transaction() antes de asignarlo. La constraint UNIQUE nunca falla." },
                { method: "GET",   path: "/api/mobiles",           desc: "Lista móviles con isActive, driverId, truckId, trailerId, operatorName. Usado para poblar selects." },
              ].map(({ method, path, desc }) => (
                <div key={path} className="flex gap-3 items-start border rounded-md p-2">
                  <span className={`text-xs px-2 py-0.5 rounded font-semibold shrink-0 ${
                    method === "POST"  ? "bg-emerald-100 text-emerald-700" :
                    method === "PATCH" ? "bg-orange-100 text-orange-700" :
                    "bg-blue-100 text-blue-700"
                  }`}>{method}</span>
                  <span className="text-xs">{path}</span>
                  <span className="text-xs text-muted-foreground font-sans ml-auto pl-4 text-right">{desc}</span>
                </div>
              ))}
            </div>
          </section>

          {/* ── Tests ── */}
          <section>
            <h2 className="text-base font-semibold mb-4">Tests</h2>
            <div className="rounded-lg border overflow-hidden text-sm">
              <table className="w-full">
                <thead className="bg-muted/50">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Archivo</th>
                    <th className="text-left px-4 py-2 font-medium text-muted-foreground">Qué cubre</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  <tr>
                    <td className="px-4 py-3 font-mono text-xs">apps/api/src/routes/shipments/shipments.test.ts</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      createShipmentSchema (clientId, workLine, batea, status, mobileId, notes, products) ·
                      updateShipmentSchema (milestone timestamps) · shipmentQuerySchema · routing POST/PATCH
                    </td>
                  </tr>
                  <tr>
                    <td className="px-4 py-3 font-mono text-xs">apps/api/src/routes/trips.test.ts</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      createTripSchema (todos los UUID fields, datetime, status, notes, null) ·
                      updateTripSchema (omite shipmentId, parcial) · tripQuerySchema · routing POST/PATCH
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            <pre className="mt-3 text-xs bg-muted rounded-md p-3 overflow-x-auto">
{`bun test apps/api/src/routes/shipments/shipments.test.ts \\
         apps/api/src/routes/trips.test.ts`}
            </pre>
          </section>

        </div>
      </div>
    </>
  )
}
